import React, { useRef, useState, useCallback } from 'react';
import { Maximize2 } from 'lucide-react';
import { MockupLightbox } from './MockupLightbox';
import { sanitizeSvg } from '../lib/sanitizeSvg';

/**
 * LiveMockupCanvas
 * ----------------
 * 1:1 mockup canvas that composites a recolored SVG over a base mockup image.
 * Supports drag-to-reposition + scale/rotation sliders when `editable=true`.
 *
 * Props:
 *  - baseSrc:        URL or data: URI for the t-shirt mockup behind the SVG.
 *                    Falls back to a plain white background when missing.
 *  - svgContent:     Raw SVG string (already recolored).
 *  - placement:      { x, y, scale, rotation } — % units (0-100) for x/y/scale.
 *  - locationLabel:  Short label rendered as a corner badge ("Front", "Back").
 *  - editable:       Enable drag handles + sliders.
 *  - onPlacementChange(next):  Fires on commit (drag end / slider release).
 *
 * Drag math: the SVG is anchored at its CENTER (translate -50%, -50%) so the
 * placement.x/y values represent "the center of the design as a % of the
 * canvas". This matches print-spec convention and keeps the scale slider
 * intuitive (the design scales from the center, not the corner).
 */
const DEFAULT_PLACEMENT = { x: 50, y: 35, scale: 25, rotation: 0 };

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const LiveMockupCanvas = ({
  baseSrc,
  svgContent,
  placement,
  locationLabel,
  editable = false,
  onPlacementChange,
  testIdPrefix = 'live-mockup',
}) => {
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  // Local state for in-flight drag/slider changes — committed on release.
  const [draft, setDraft] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const eff = draft || placement || DEFAULT_PLACEMENT;

  // Drag implementation: capture pointer at down, translate move deltas
  // into % of the canvas, commit on up. Pointer events so it works on
  // both touch + mouse without separate handlers.
  const onPointerDown = useCallback(
    (e) => {
      if (!editable || !wrapRef.current) return;
      e.preventDefault();
      const rect = wrapRef.current.getBoundingClientRect();
      dragRef.current = {
        rect,
        startX: e.clientX,
        startY: e.clientY,
        startPlacement: { ...(draft || placement || DEFAULT_PLACEMENT) },
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* setPointerCapture may throw on synthetic / read-only events */
      }
    },
    [editable, draft, placement]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragRef.current) return;
      const { rect, startX, startY, startPlacement } = dragRef.current;
      const dxPct = ((e.clientX - startX) / rect.width) * 100;
      const dyPct = ((e.clientY - startY) / rect.height) * 100;
      setDraft({
        ...startPlacement,
        x: clamp(startPlacement.x + dxPct, 0, 100),
        y: clamp(startPlacement.y + dyPct, 0, 100),
      });
    },
    []
  );

  const onPointerUp = useCallback(
    () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      if (draft && onPlacementChange) onPlacementChange(draft);
    },
    [draft, onPlacementChange]
  );

  // Reset the draft whenever the parent feeds in a new placement (e.g. after
  // server-side commit returned the canonical value). We rely on commitSlider
  // / onPointerUp to null `draft` before the parent re-renders, so we don't
  // need a useEffect to mirror props (which would trigger the
  // react-hooks/set-state-in-effect lint).

  const updateSlider = (field, value) => {
    const startPlacement = draft || placement || DEFAULT_PLACEMENT;
    setDraft({ ...startPlacement, [field]: value });
  };
  const commitSlider = (field, value) => {
    const startPlacement = placement || DEFAULT_PLACEMENT;
    const next = { ...startPlacement, [field]: value };
    setDraft(null);
    onPlacementChange && onPlacementChange(next);
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div
        ref={wrapRef}
        className="relative w-full overflow-hidden rounded-lg border border-gray-200 bg-white select-none"
        style={{ aspectRatio: '1 / 1', maxWidth: 420 }}
        data-testid={`${testIdPrefix}-canvas`}
      >
        {baseSrc ? (
          <img
            src={baseSrc}
            alt="mockup"
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-xs text-gray-400">
            No base mockup
          </div>
        )}
        {/* SVG overlay — pointer events ON only when editable so it doesn't
            steal clicks from the underlying t-shirt image picker. */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className={editable ? 'cursor-grab active:cursor-grabbing' : 'pointer-events-none'}
          style={{
            position: 'absolute',
            left: `${eff.x}%`,
            top: `${eff.y}%`,
            width: `${eff.scale}%`,
            transform: `translate(-50%, -50%) rotate(${eff.rotation || 0}deg)`,
            touchAction: 'none',
          }}
          data-testid={`${testIdPrefix}-overlay`}
          dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgContent) }}
        />
        {locationLabel && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-black/70 text-white">
            {locationLabel}
          </div>
        )}
        {/* Maximize → opens shared lightbox with a snapshot of the composite.
            Lives outside the drag overlay so it never steals a drag start. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
          className="absolute top-2 right-2 bg-black/70 text-white rounded p-1.5 hover:bg-black/90 transition"
          title="Zoom"
          data-testid={`${testIdPrefix}-zoom`}
        >
          <Maximize2 size={14} />
        </button>
      </div>

      <MockupLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        title={locationLabel ? `${locationLabel} mockup` : 'Mockup'}
        // Render the same composite (base + SVG overlay) inside the lightbox
        // by serializing the visible state into an SVG wrapper. Keeps the
        // zoom logic shared with raster lightboxes.
        svgContent={`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000" style="background:#fff">
          ${baseSrc ? `<image href="${baseSrc}" x="0" y="0" width="1000" height="1000" preserveAspectRatio="xMidYMid meet"/>` : ''}
          <g transform="translate(${eff.x * 10}, ${eff.y * 10})">
            <g transform="rotate(${eff.rotation || 0}) scale(${eff.scale / 100})">
              <g transform="translate(-500, -500)">
                ${(svgContent || '').replace(/<\?xml[^?]*\?>/g, '')}
              </g>
            </g>
          </g>
        </svg>`}
      />

      {editable && (
        <div className="grid grid-cols-2 gap-3 text-xs">
          <label className="flex flex-col gap-1">
            <span className="text-gray-600">
              Size <span className="text-gray-400">({Math.round(eff.scale)}%)</span>
            </span>
            <input
              type="range"
              min={5}
              max={70}
              step={1}
              value={eff.scale}
              onChange={(e) => updateSlider('scale', parseFloat(e.target.value))}
              onMouseUp={(e) => commitSlider('scale', parseFloat(e.target.value))}
              onTouchEnd={(e) => commitSlider('scale', parseFloat(e.target.value))}
              data-testid={`${testIdPrefix}-scale`}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-gray-600">
              Rotation <span className="text-gray-400">({Math.round(eff.rotation || 0)}°)</span>
            </span>
            <input
              type="range"
              min={-180}
              max={180}
              step={5}
              value={eff.rotation || 0}
              onChange={(e) => updateSlider('rotation', parseFloat(e.target.value))}
              onMouseUp={(e) => commitSlider('rotation', parseFloat(e.target.value))}
              onTouchEnd={(e) => commitSlider('rotation', parseFloat(e.target.value))}
              data-testid={`${testIdPrefix}-rotation`}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default LiveMockupCanvas;
